import {CUSTOM_COLORS} from "@/core/services/palettes.generated";

// The colour of the accent that the user chooses. The styles of the site have the brand blue written into them as
// fixed numbers; for the colour of the user a generated file (public/css/palette.custom.css, see
// scripts/build-palettes.js) has a CSS variable in every such place. This file computes the values of the variables.
//
// The maths is the one of the script: a colour of the styles is moved to the hue of the chosen colour, and its
// LIGHTNESS is changed so that the relative luminance stays the same. Therefore every pair of colours keeps its contrast
// (text 4.5:1 stays 4.5:1) whatever the user picks.

type Rgb = [number, number, number];

export const toRgb = (hex: string): Rgb => {
    const h = hex.replace("#", "");
    return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16)) as Rgb;
};

export const toHex = (rgb: number[]): string =>
    "#" + rgb.map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0")).join("");

export const isHexColor = (value: unknown): value is string => typeof value === "string" && /^#[0-9a-fA-F]{6}$/.test(value);

const toHsl = ([r, g, b]: number[]): [number, number, number] => {
    r /= 255;
    g /= 255;
    b /= 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    const d = max - min;
    if (d === 0) return [0, 0, l];
    const s = d / (1 - Math.abs(2 * l - 1));
    let h;
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    return [(h * 60 + 360) % 360, s, l];
};

const fromHsl = (h: number, s: number, l: number): Rgb => {
    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = l - c / 2;
    const [r, g, b] =
        h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x] : h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x];
    return [(r + m) * 255, (g + m) * 255, (b + m) * 255];
};

const luminance = (rgb: number[]): number => {
    const [r, g, b] = rgb.map((v) => {
        v /= 255;
        return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

// the same colour in another hue, with the same relative luminance
export const shiftHue = (rgb: number[], hue: number): number[] => {
    const [, s] = toHsl(rgb);
    const target = luminance(rgb);
    let lo = 0;
    let hi = 1;
    for (let i = 0; i < 24; i++) {
        const mid = (lo + hi) / 2;
        if (luminance(fromHsl(hue, s, mid)) < target) lo = mid;
        else hi = mid;
    }
    const out = fromHsl(hue, s, (lo + hi) / 2);
    return Number.isFinite(out[0]) ? out.map(Math.round) : rgb;
};

// the hue of the colour that the user picked
export const hueOf = (hex: string): number => toHsl(toRgb(hex))[0];

// The values of all the variables of palette.custom.css for a chosen colour: --pcl-N / --pcl-N-rgb (the light theme) and
// --pcd-N / --pcd-N-rgb (the dark one).
export const customVariables = (hex: string): Record<string, string> => {
    const hue = hueOf(hex);
    const out: Record<string, string> = {};

    (["light", "dark"] as const).forEach((theme) => {
        const prefix = theme === "dark" ? "pcd" : "pcl";
        CUSTOM_COLORS[theme].forEach((rgb, i) => {
            const next = shiftHue(rgb, hue);
            out[`--${prefix}-${i}`] = toHex(next);
            out[`--${prefix}-${i}-rgb`] = next.join(",");
        });
    });

    return out;
};

// the colour of the accent as it is in the two themes (for the dots of the picker and for the charts)
export const customSwatch = (hex: string): {light: string; dark: string} => {
    const hue = hueOf(hex);
    return {light: toHex(shiftHue(toRgb("#006fb5"), hue)), dark: toHex(shiftHue(toRgb("#3699ff"), hue))};
};
